import type { HTMLAttributes, ReactNode } from 'react';

import classNames from 'classnames';

import styles from './Grid.module.css';

import type { SpacingProps } from '../../styles/spacing';
import { getSpacingStyles } from '../../styles/spacing';
import { getStyles as getFlexBoxStyles } from '../Flex/getStyles';
import type { FlexResponsiveProps } from '../Flex/types';
import { getGapStyles } from './gap';
import type { GridGrowProps } from './grow';
import { getGrowStyles } from './grow';

/** The props for the {@link Grid} component. */
export interface GridProps
  extends
    FlexResponsiveProps,
    GridGrowProps,
    SpacingProps,
    HTMLAttributes<HTMLDivElement> {
  className?: string;
  /** Only use `GridItem` components as children of `Grid`. */
  children: ReactNode;

  /** @deprecated Use `justify` instead */
  justifyContent?: FlexResponsiveProps['justify'];
  /** @deprecated Use `direction` instead */
  flexDirection?: FlexResponsiveProps['direction'];
}

/**
 * Use `Grid` as container of one or more `GridItem`s to split the viewport
 * horizontally up to 12 columns.
 *
 * `Grid` creates a flexbox container for building a grid layout. It must be
 * used in pair with `GridItem`.
 *
 * @example
 *
 * ```
 * <Grid>
 *   <GridItem md={6}>Left column on medium viewports</GridItem>
 *   <GridItem md={6}>Right column on medium viewports</GridItem>
 *   <GridItem lg={4}>Left column on large viewports</GridItem>
 *   <GridItem lg={8}>Right column on large viewports</GridItem>
 * </Grid>;
 * ```
 *
 * @see https://corgi-x.tfd.engineering/components/grid
 */
export function Grid(props: GridProps) {
  const {
    children,

    columnGap,
    rowGap,
    gap,

    spacing,
    hSpacing,
    vSpacing,
    topSpacing,
    bottomSpacing,
    leftSpacing,
    rightSpacing,

    justify,
    direction,
    justifyContent,
    flexDirection,
    alignItems,
    alignContent,
    wrap,

    growContent,

    className,
    ...divAttributes
  } = props;

  const flexboxStyles = getFlexBoxStyles({
    justify: justifyContent ?? justify,
    direction: flexDirection ?? direction,
    alignItems,
    alignContent,
    wrap,
  });
  const growStyles = getGrowStyles({ growContent });
  const gapStyles = getGapStyles({ columnGap, rowGap, gap });
  const spacingStyles = getSpacingStyles('padding', props);

  const cssClass = classNames(
    styles.root,
    flexboxStyles,
    gapStyles,
    growStyles,
    spacingStyles,
    className
  );

  return (
    <div {...divAttributes} className={cssClass}>
      {children}
    </div>
  );
}
